import { ReactElement, ReactNode } from 'react'
import { Popover, Stack, Typography } from '@mui/material'

export interface PopoverPanelProps {
  /** Element the popover is anchored to; `null` keeps it closed. */
  anchorEl: HTMLElement | null
  onClose: () => void
  /** Bold subtitle rendered above the content. */
  title: string
  /** `data-cy` selector forwarded to the popover root. */
  dataCy?: string
  children: ReactNode
}

/**
 * Small header popover: a bottom-right anchored, non-portaled `Popover` with themed paper padding
 * and a title + content Stack. Shared by the IFC storey controls' settings and filter popovers.
 */
export const PopoverPanel = ({
  anchorEl,
  onClose,
  title,
  dataCy,
  children,
}: PopoverPanelProps): ReactElement => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    disablePortal
    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    slotProps={{
      paper: {
        sx: theme => ({
          p: 1.5,
          bgcolor: theme.palette.background.paper,
          backgroundImage: 'none',
        }),
      },
    }}
    data-cy={dataCy}
  >
    <Stack direction="column" spacing={1}>
      <Typography variant="subtitle2">{title}</Typography>
      {children}
    </Stack>
  </Popover>
)
